import { Cookies } from "react-cookie";
import { proxy, snapshot, useSnapshot } from "valtio";
import { ProductName } from "@airhelp/plus";
import { AhpCurrency, ahPlusCurrencies } from "app-settings/localesAndCurrencies";
import { IOffer, IPrice } from "api/model/AhPlusOffersRest";
import ahplusOffersApi from "../api/ahplusOffers";

const { VITE_AHPLUS_SMART_ANNUAL_PRICE_EUR, VITE_AHPLUS_LITE_ANNUAL_PRICE_EUR } = import.meta.env;
const domain = import.meta.env.VITE_EMBER_REGULATIONS_COOKIE_DOMAIN;

const AHP_CURRENCY_COOKIE = "ahplus_currency";
const DEFAULT_AHP_CURRENCY = "EUR";

const cookies = new Cookies();

export const formatPriceWithCurrency = (price: number, currency: AhpCurrency) => {
  if (!price || !currency) {
    return "";
  }

  const currencySettings =
    ahPlusCurrencies[currency.toUpperCase() as AhpCurrency] ?? ahPlusCurrencies.EUR;
  const parsedPrice = parseFloat(price.toString());
  const formattedValue = parsedPrice.toFixed(2);

  return currencySettings.symbolPosition === "before"
    ? `${currencySettings.symbol}${formattedValue}`
    : `${formattedValue} ${currencySettings.symbol}`;
};

type AhplusSettingsState = {
  currency: AhpCurrency;
  canBuyAhPlus: boolean;
  offers: IOffer[];
};

const ahplusSettings: AhplusSettingsState = {
  currency: cookies.get(AHP_CURRENCY_COOKIE) || DEFAULT_AHP_CURRENCY,
  canBuyAhPlus: true,
  offers: [],
};

const ahplusState = proxy(ahplusSettings);

const setAhPlusCurrencyCookie = (currency: AhpCurrency) => {
  cookies.set(AHP_CURRENCY_COOKIE, currency, { path: "/", domain });
};

const calculatePrices = (price: number) => {
  const annualPrice = price;
  const monthlyPrice = Number(annualPrice) / 12;

  return { annualPrice, monthlyPrice };
};

export const setAhplusCurrency = (currency: AhpCurrency) => {
  ahplusState.currency = currency;

  setAhPlusCurrencyCookie(currency);
};

export const setAhPlusOffers = (offers: IOffer[]) => {
  ahplusState.offers = offers;
};

export const setCanBuyAhPlus = (value: boolean) => {
  ahplusState.canBuyAhPlus = value;
};

export const getCanBuyAhPlus = () => {
  return snapshot(ahplusState).canBuyAhPlus;
};

export const useProductPrices = (productName: ProductName) => {
  const { currency, offers } = useSnapshot(ahplusState);

  const planOffer = offers.find((data) => data.name === productName);

  const price = planOffer?.prices.find(({ code }: IPrice) => currency === code);
  const currencyPrice = (price?.code ?? DEFAULT_AHP_CURRENCY) as AhpCurrency;
  const priceInCurrency =
    price?.price ??
    (productName === ProductName.AIRHELP_LITE_ANNUALLY
      ? VITE_AHPLUS_LITE_ANNUAL_PRICE_EUR
      : VITE_AHPLUS_SMART_ANNUAL_PRICE_EUR);

  const { annualPrice, monthlyPrice } = calculatePrices(priceInCurrency);

  return { currencyPrice, annualPrice, monthlyPrice };
};

export async function loadAhplusOffers() {
  const ahpOffers = await ahplusOffersApi.fetchOffers();

  setCanBuyAhPlus(ahpOffers.canBuyPlus);
  setAhPlusOffers(ahpOffers.offers);

  return ahpOffers;
}

export const useAhplusSettingsSnapshot = () => useSnapshot(ahplusState);
